import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { LineMask, FadeIn } from "@/components/Reveal";

const ABOUT_VIDEO = `${process.env.REACT_APP_BACKEND_URL}/api/media/london-tower-bridge.mp4`;
const ABOUT_VIDEO_POSTER = "/media/london-tower-bridge-poster.jpg";

const chapters = [
  {
    number: "01",
    title: "Scrutiny with purpose",
    text: "Independent scrutiny should do more than identify issues. It should provide clear, achievable recommendations that support lasting improvement — and leave services stronger than it found them.",
  },
  {
    number: "02",
    title: "Evidence-informed, always",
    text: "Our advice draws on extensive experience in youth justice, management, quality assurance and performance improvement — grounded in research and what is known to work.",
  },
  {
    number: "03",
    title: "Tailored, never templated",
    text: "Every commission is shaped around the client's needs. No off-the-shelf frameworks, no boilerplate reports — practical solutions built for your service and your context.",
  },
  {
    number: "04",
    title: "Impact you can measure",
    text: "Better outcomes for children, victims and communities are the test of everything we do. We define success together, then help you evidence it.",
  },
];

const partners = [
  "Local authorities",
  "Youth Justice Services",
  "Youth Justice Management Boards",
  "Police, probation, health & education partners",
  "Regional partnerships across England & Wales",
];

export default function About() {
  return (
    <div data-testid="about-page">
      <section className="bg-paper pt-44 pb-20 lg:pb-28" data-testid="about-hero">
        <div className="mx-auto max-w-[1600px] px-6 lg:px-12 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-3">
            <FadeIn>
              <p className="font-mono text-[11px] tracking-[0.24em] uppercase text-brand">
                About
              </p>
            </FadeIn>
          </div>
          <div className="md:col-span-8 md:col-start-5">
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.98] tracking-tight text-ink">
              <LineMask delay={0.2} lines={["A trusted partner", "for Youth Justice."]} />
            </h1>
            <FadeIn delay={0.5} className="mt-8 max-w-2xl">
              <p className="text-base sm:text-lg leading-relaxed text-ink/65">
                KC Consultancy is a specialist consultancy dedicated to improving the
                quality and effectiveness of Youth Justice Services across England and
                Wales — providing independent capacity, strengthening practice and
                governance, and helping services prepare confidently for inspection.
              </p>
            </FadeIn>
          </div>
        </div>
      </section>

      <section className="bg-paper pb-28 lg:pb-40" data-testid="about-london-video">
        <div className="mx-auto max-w-[1600px] px-6 lg:px-12">
          <FadeIn>
            <div className="relative overflow-hidden border border-ink/10 bg-ink">
              <video
                className="h-[360px] sm:h-[480px] lg:h-[560px] w-full object-cover"
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                poster={ABOUT_VIDEO_POSTER}
                aria-label="Tower Bridge in London at dusk, with traffic and boats passing along the Thames"
                data-testid="about-london-video-player"
              >
                <source src={ABOUT_VIDEO} type="video/mp4" />
              </video>
              <div className="absolute inset-x-0 bottom-0 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 bg-ink/55 px-6 py-5 backdrop-blur-sm">
                <p className="font-mono text-[10px] tracking-[0.22em] uppercase text-paper/70">
                  London roots · England &amp; Wales reach
                </p>
                <p className="font-display text-lg sm:text-xl text-paper">
                  Tower Bridge at dusk
                </p>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      <section className="noise-overlay bg-ink py-28 lg:py-40 text-paper" data-testid="about-manifesto">
        <div className="relative z-10 mx-auto max-w-[1600px] px-6 lg:px-12">
          <FadeIn>
            <p className="font-mono text-[11px] tracking-[0.24em] uppercase text-brand">
              The manifesto
            </p>
          </FadeIn>
          <div className="mt-16">
            {chapters.map((chapter, i) => (
              <FadeIn key={chapter.number} delay={i * 0.05}>
                <div
                  className="grid grid-cols-1 md:grid-cols-12 gap-6 border-t border-white/10 py-14 lg:py-20 last:border-b"
                  data-testid={`manifesto-chapter-${chapter.number}`}
                >
                  <p className="md:col-span-2 font-mono text-5xl lg:text-7xl text-brand/90 leading-none">
                    {chapter.number}
                  </p>
                  <h2 className="md:col-span-4 font-display text-3xl sm:text-4xl leading-tight">
                    {chapter.title}
                  </h2>
                  <p className="md:col-span-5 md:col-start-8 text-base leading-relaxed text-paper/65">
                    {chapter.text}
                  </p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-paper py-28 lg:py-40" data-testid="about-founder">
        <div className="mx-auto max-w-[1600px] px-6 lg:px-12 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-3">
            <FadeIn>
              <p className="font-mono text-[11px] tracking-[0.24em] uppercase text-brand">
                The founder
              </p>
            </FadeIn>
          </div>
          <div className="md:col-span-8 md:col-start-5">
            <h2 className="font-display font-semibold text-4xl sm:text-5xl lg:text-6xl tracking-tight text-ink">
              <LineMask inView lines={["Meet the founder."]} />
            </h2>
            <FadeIn delay={0.2}>
              <p
                className="mt-4 font-mono text-[11px] tracking-[0.22em] uppercase text-slateblue"
                data-testid="founder-role"
              >
                Founder &amp; Principal Consultant
              </p>
            </FadeIn>
            <FadeIn delay={0.3} className="mt-8 max-w-2xl">
              <p className="text-base sm:text-lg leading-relaxed text-ink/65">
                KC Consultancy was founded by an experienced Youth Justice leader
                with deep expertise in quality assurance, inspection readiness and
                practice improvement. With over 19 years' experience, the founder has
                supported Youth Justice Services to strengthen practice, improve
                performance and deliver better outcomes for children, victims and
                communities.
              </p>
              <p className="mt-5 text-base sm:text-lg leading-relaxed text-ink/65">
                That experience spans frontline practice — including gangs work,
                high-safety caseloads, violence prevention and safeguarding — as well as
                court services, management, quality assurance and strategic leadership,
                across numerous London boroughs: leading service improvement
                initiatives, strengthening governance and embedding quality assurance
                frameworks that drive continuous improvement.
              </p>
              <p className="mt-5 text-base sm:text-lg leading-relaxed text-ink/65">
                This includes extensive experience supporting services before and after
                HM Inspectorate of Probation (HMIP) inspections — leading multi-agency
                audits, independent quality assurance reviews, casework evaluation
                against the Case Assessment Rules and Guidance (CARaG), oversight of
                work with victims under the Victims' Standard, inspection readiness
                support, and robust post-inspection improvement plans. It also
                includes joint auditing with children's social care, applying a youth
                justice lens to shared areas of practice and safeguarding. These
                approaches have supported Youth Justice Services to secure Good
                inspection outcomes, including Outstanding features and success under
                current HMIP inspection criteria.
              </p>
              <p className="mt-5 text-base sm:text-lg leading-relaxed text-ink/65">
                Through national implementation consultancy, our founder supported
                more than 100 Youth Justice Services across England and Wales with case
                management system implementation — national experience that brings
                valuable insight into the strengths, challenges and emerging practice
                across Youth Justice Services, enabling informed, evidence-based
                solutions tailored to each organisation.
              </p>
              <p className="mt-5 text-base sm:text-lg leading-relaxed text-ink/65">
                Today, KC Consultancy works collaboratively with leaders and
                practitioners to provide independent quality assurance, inspection
                readiness, mock inspections, audits, case reviews, improvement planning,
                training and strategic advice. The approach is practical, supportive and
                focused on achieving lasting improvements that are both measurable and
                sustainable. KC Consultancy is also stepping into children's services
                auditing, working to Ofsted standards.
              </p>
              <p className="mt-5 text-base sm:text-lg leading-relaxed text-ink/65">
                Current professional development includes the Youth Justice Board (YJB)
                Elevate Programme — further developing leadership capability, strategic
                thinking and commitment to continuous improvement within Youth Justice.
              </p>
            </FadeIn>
            <FadeIn delay={0.4} className="mt-12 max-w-2xl">
              <p className="font-mono text-[11px] tracking-[0.24em] uppercase text-brand">
                Qualifications &amp; Development
              </p>
              <ul className="mt-5 border-t border-ink/10" data-testid="founder-qualifications">
                <li className="flex items-baseline gap-5 border-b border-ink/10 py-4" data-testid="qualification-1">
                  <span className="font-mono text-xs text-slateblue">01</span>
                  <span className="text-sm sm:text-base text-ink/80">
                    Youth Justice Board (YJB) Elevate Programme
                    <span className="ml-2 font-mono text-[10px] tracking-[0.18em] uppercase text-teal">In progress</span>
                  </span>
                </li>
                <li className="flex items-baseline gap-5 border-b border-ink/10 py-4" data-testid="qualification-2">
                  <span className="font-mono text-xs text-slateblue">02</span>
                  <span className="text-sm sm:text-base text-ink/80">BA (Hons) Criminology</span>
                </li>
                <li className="flex items-baseline gap-5 border-b border-ink/10 py-4" data-testid="qualification-3">
                  <span className="font-mono text-xs text-slateblue">03</span>
                  <span className="text-sm sm:text-base text-ink/80">Graduate Diploma in Law (GDL)</span>
                </li>
                <li className="flex items-baseline gap-5 border-b border-ink/10 py-4" data-testid="qualification-4">
                  <span className="font-mono text-xs text-slateblue">04</span>
                  <span className="text-sm sm:text-base text-ink/80">Effective Practice in Youth Justice</span>
                </li>
              </ul>
            </FadeIn>
          </div>
        </div>
        <div className="mx-auto max-w-[1600px] px-6 lg:px-12 mt-20">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-ink/10 border border-ink/10">
            <FadeIn delay={0.1} className="bg-paper">
              <div className="p-8 lg:p-10" data-testid="founder-highlight-1">
                <p className="font-display font-semibold text-3xl sm:text-4xl text-ink">19+</p>
                <p className="mt-3 font-mono text-[10px] tracking-[0.18em] uppercase text-slateblue">
                  Years in youth justice
                </p>
              </div>
            </FadeIn>
            <FadeIn delay={0.18} className="bg-paper">
              <div className="p-8 lg:p-10" data-testid="founder-highlight-2">
                <p className="font-display font-semibold text-3xl sm:text-4xl text-ink">100+</p>
                <p className="mt-3 font-mono text-[10px] tracking-[0.18em] uppercase text-slateblue">
                  Youth Justice Services supported nationally
                </p>
              </div>
            </FadeIn>
            <FadeIn delay={0.26} className="bg-paper">
              <div className="p-8 lg:p-10" data-testid="founder-highlight-3">
                <p className="font-display font-semibold text-3xl sm:text-4xl text-brand">Good</p>
                <p className="mt-3 font-mono text-[10px] tracking-[0.18em] uppercase text-slateblue">
                  Inspection outcome contributed to — with Outstanding elements
                </p>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      <section className="bg-cream py-28 lg:py-40" data-testid="about-partners">
        <div className="mx-auto max-w-[1600px] px-6 lg:px-12 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <FadeIn>
              <p className="font-mono text-[11px] tracking-[0.24em] uppercase text-brand">
                Who we work with
              </p>
              <h2 className="mt-6 font-display text-4xl sm:text-5xl leading-tight text-ink">
                <LineMask inView lines={["Alongside the people", "who deliver youth justice."]} />
              </h2>
            </FadeIn>
          </div>
          <div className="md:col-span-6 md:col-start-7">
            <ul className="border-t border-ink/10">
              {partners.map((partner, i) => (
                <FadeIn key={partner} delay={i * 0.06}>
                  <li
                    className="flex items-baseline gap-6 border-b border-ink/10 py-6"
                    data-testid={`partner-${i}`}
                  >
                    <span className="font-mono text-sm text-slateblue">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="font-display text-xl sm:text-2xl text-ink">{partner}</span>
                  </li>
                </FadeIn>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="bg-paper py-28 lg:py-36" data-testid="about-cta">
        <div className="mx-auto max-w-[1600px] px-6 lg:px-12 grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
          <h2 className="md:col-span-8 font-display text-4xl sm:text-5xl leading-tight text-ink">
            <LineMask inView lines={["Additional independent capacity,", "when you need it most."]} />
          </h2>
          <FadeIn delay={0.2} className="md:col-span-4 md:justify-self-end">
            <Link
              to="/contact"
              data-testid="about-cta-button"
              className="inline-flex items-center gap-2 rounded-full bg-ink px-8 py-4 text-sm tracking-wide text-paper transition-colors duration-300 hover:bg-brand"
            >
              Contact Us
              <ArrowUpRight className="h-4 w-4" />
            </Link>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}
